import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Attachments } from '../components/Attachments';
import { Icon } from '../components/Icon';
import { api, ApiError } from '../lib/api';
import { amount } from '../lib/format';
import '../styles/templates.css';

const PREVIEW = Boolean(import.meta.env.VITE_PREVIEW);

interface DocLine { description: string; quantity: number; unit: string; unitPriceMinor: number; taxPct: number; lineTotalMinor: number }
interface DocView {
  number: string; typeLabel: string; statusLabel: string; title: string | null; body: string | null;
  issueDate: string | null; dueDate: string | null; currency: string; counterparty: string | null;
  lines: DocLine[];
  totals: { subtotalMinor: number; discountMinor: number; taxMinor: number; totalMinor: number };
  company: { name: string; tradeName: string | null; logoUrl: string | null; brandColor: string; footer: string; bankLine: string; taxNumber: string | null };
  allowDownload: boolean;
}

const fmtDate = (iso: string | null): string => (iso ? new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' }) : '—');

/** Detecta si el contenido es HTML (del editor rico) y no texto plano. */
function isHtml(s: string): boolean {
  return /<(p|h[1-6]|ul|ol|li|strong|em|u|br|blockquote|table|thead|tbody|tr|td|th|hr|div|span|img|font|mark)[\s>]/i.test(s);
}

/** Si el HTML llegó escapado (&lt;p&gt;...), lo desescapa para poder pintarlo. */
function unescapeIfNeeded(s: string): string {
  // Si no hay '<' real pero sí '&lt;', es HTML escapado: hay que revertirlo.
  if (!/</.test(s) && /&lt;/.test(s)) {
    return s.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, '&');
  }
  return s;
}

function Clauses({ text }: { text: string }) {
  const content = unescapeIfNeeded(text);
  // Contenido del editor rico: se muestra como HTML (tablas, colores, imágenes).
  if (isHtml(content)) {
    return <div className="dv-body__rich" dangerouslySetInnerHTML={{ __html: content }} />;
  }
  return (
    <>
      {content.replace(/\r/g, '').split('\n').map((raw, i) => {
        const line = raw.trim();
        if (!line) return null;
        const letters = line.replace(/[^A-Za-zÁÉÍÓÚÑÜáéíóúñü]/g, '');
        const isHeading = letters.length > 2 && letters === letters.toUpperCase() && line.length < 70;
        return isHeading
          ? <div key={i} className="dv-clause">{line}</div>
          : <p key={i} className="dv-para">{line}</p>;
      })}
    </>
  );
}

export function DocView() {
  const { token } = useParams<{ token: string }>();
  const [doc, setDoc] = useState<DocView | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.get<DocView>(`/public/doc/${token}`).then(setDoc).catch((e) => setErr(e instanceof ApiError ? e.message : 'No se pudo cargar el documento.'));
  }, [token]);

  const download = () => {
    if (PREVIEW) { window.print(); return; }
    window.open(`${window.location.origin}/api/public/doc/${token}/pdf`, '_blank');
  };

  if (err) {
    return (
      <div className="sign-page">
        <div className="sign-shell" style={{ maxWidth: 420, textAlign: 'center', marginTop: 80 }}>
          <div className="sign-gate__ico" style={{ background: 'var(--clay-tint)', color: 'var(--clay)' }}><Icon name="alert" size={26} /></div>
          <h1 className="h2" style={{ marginBottom: 8 }}>Enlace no disponible</h1>
          <p className="muted">{err}</p>
        </div>
      </div>
    );
  }
  if (!doc) return <div className="sign-page"><div className="faint" style={{ marginTop: 80 }}>Cargando documento…</div></div>;

  const accent = doc.company.brandColor || '#205c3e';

  return (
    <div className="sign-page">
      <div className="sign-shell">
        <div className="sign-brandbar dv-noprint" style={{ background: accent }} />

        <div className="dv-doc" style={{ ['--accent' as string]: accent } as React.CSSProperties}>
          <div className="dv-head">
            <div>
              {doc.company.logoUrl && <img className="dv-logo" src={doc.company.logoUrl} alt="" />}
              <div className="dv-company" style={{ color: accent }}>{doc.company.tradeName ?? doc.company.name}</div>
              <div className="dv-company__sub">{doc.company.taxNumber ? `TRN ${doc.company.taxNumber}` : ''}</div>
            </div>
            <div className="dv-num--top">{doc.number}</div>
          </div>

          <div className="dv-rules" style={{ ['--accent' as string]: accent } as React.CSSProperties} />

          <div className="row between" style={{ alignItems: 'flex-start', gap: 20 }}>
            <div>
              <div className="dv-kicker">{doc.typeLabel}</div>
              <div className="dv-type">{doc.number}</div>
              {doc.title && <div className="dv-title">{doc.title}</div>}
            </div>
            <div className="dv-status" style={{ color: accent, whiteSpace: 'nowrap' }}>{doc.statusLabel}</div>
          </div>

          <div className="dv-grid">
            <div className="dv-cell">
              <div className="dv-cell__k">Issue date</div>
              <div className="dv-cell__v">{fmtDate(doc.issueDate)}</div>
            </div>
            <div className="dv-cell">
              <div className="dv-cell__k">{doc.dueDate ? 'Vencimiento' : 'Validez'}</div>
              <div className="dv-cell__v">{fmtDate(doc.dueDate)}</div>
            </div>
            <div className="dv-cell">
              <div className="dv-cell__k">Moneda</div>
              <div className="dv-cell__v">{doc.currency}</div>
            </div>
            <div className="dv-cell">
              <div className="dv-cell__k">Importe total</div>
              <div className="dv-cell__v dv-cell__v--strong">{amount(doc.totals.totalMinor, doc.currency)}</div>
            </div>
          </div>

          <div className="dv-parties">
            <div>
              <div className="dv-to__label">{doc.typeLabel.toLowerCase().includes('factura') ? 'Facturar a' : 'Destinatario'}</div>
              <div className="dv-to__name">{doc.counterparty ?? '—'}</div>
            </div>
            <div>
              <div className="dv-to__label">Emitido por</div>
              <div className="dv-to__name">{doc.company.name}</div>
              <div className="dv-to__lines">{doc.company.taxNumber ? `TRN ${doc.company.taxNumber}` : ''}</div>
            </div>
          </div>

          <div className="dv-sec">Items</div>

          <table className="dv-table">
            <thead>
              <tr>
                <th>Description</th>
                <th className="r">Cant.</th>
                <th className="r">Precio</th>
                <th className="r">IVA</th>
                <th className="r">Total</th>
              </tr>
            </thead>
            <tbody>
              {doc.lines.map((l, i) => (
                <tr key={i}>
                  <td>{l.description}</td>
                  <td className="r num">{l.quantity}</td>
                  <td className="r num">{amount(l.unitPriceMinor, doc.currency)}</td>
                  <td className="r num">{l.taxPct}%</td>
                  <td className="r num" style={{ fontWeight: 600 }}>{amount(l.lineTotalMinor, doc.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="dv-totals">
            <div className="dv-totals__row"><span className="k">Subtotal</span><span className="v">{amount(doc.totals.subtotalMinor, doc.currency)}</span></div>
            {doc.totals.discountMinor > 0 && <div className="dv-totals__row"><span className="k">Descuento</span><span className="v">− {amount(doc.totals.discountMinor, doc.currency)}</span></div>}
            <div className="dv-totals__row"><span className="k">Impuestos</span><span className="v">{amount(doc.totals.taxMinor, doc.currency)}</span></div>
            <div className="dv-totals__row dv-totals__grand" style={{ borderColor: accent }}><span>Total</span><span className="v">{amount(doc.totals.totalMinor, doc.currency)} {doc.currency}</span></div>
          </div>

          {doc.body && (
            <div className="dv-body">
              <div className="dv-body__label">{doc.typeLabel.toLowerCase().includes('cotiza') ? 'Propuesta' : 'Terms & Conditions'}</div>
              <Clauses text={doc.body} />
            </div>
          )}

          {(doc.company.bankLine || doc.company.footer) && (
            <div className="dv-foot">
              {doc.company.bankLine && <div>{doc.company.bankLine}</div>}
              {doc.company.footer && <div>{doc.company.footer}</div>}
            </div>
          )}

          {/* Documentos adjuntos visibles para el cliente */}
          <div className="dv-attach">
            <Attachments shareToken={token} readOnly />
          </div>
        </div>

        {doc.allowDownload && (
          <div className="dv-noprint" style={{ textAlign: 'center', marginTop: 24 }}>
            <button className="btn btn--primary" onClick={download}>
              <Icon name="download" size={16} /> Descargar PDF
            </button>
          </div>
        )}
        <div className="sign-foot dv-noprint">Documento emitido por {doc.company.tradeName ?? doc.company.name} · generado con Nexo</div>
      </div>
    </div>
  );
}
